import { useAtom } from 'jotai';
import { MultiSelectFilter } from 'components/multi-select-filter';
import { useDevelopmentAreaTagsAtom } from './show/atoms';

/** What the filter reads while nothing is picked: every area, whatever its tags. */
const TAGS_FILTER_LABEL = 'Any';
/** The label above the filter, the same as the Tract Catalog's Tags filter. */
const TAGS_FILTER_TITLE = 'Tags';

/**
 * The Development Area Catalog's Tags filter: a multi-select of the tags in use on development
 * areas. The list narrows to the areas carrying ANY picked tag; none picked reads Any. It loads its
 * own options once per visit, so an alias added on an area's page shows when the catalog is opened
 * again. The control itself is the MultiSelectFilter that StateFilter also uses.
 * @param {Object} props - The component props.
 * @param {string[]} props.selectedTags - The picked tags, as their ids (normalized names).
 * @param {Function} props.onChange - Called with the ids of all picked tags when the pick changes.
 * @return {JSX.Element} The Tags filter.
 */
export function TagsFilter({
  selectedTags,
  onChange,
}: {
  selectedTags: string[];
  onChange: (ids: string[]) => void;
}) {
  const [resource, refetch] = useAtom(useDevelopmentAreaTagsAtom());
  const data = resource.data;
  const tags = data && 'results' in data ? data.results : [];
  const error = data && 'error' in data ? 'Unable to display the tags.' : null;

  return (
    <MultiSelectFilter
      id="tags-multiselect"
      testIdPrefix="tags-filter"
      items={tags}
      getKey={(tag) => tag.id}
      selectedKeys={selectedTags}
      onChange={onChange}
      title={TAGS_FILTER_TITLE}
      label={TAGS_FILTER_LABEL}
      manyLabel={(count) => `${count} tags`}
      errorLabel="Error fetching tags"
      loading={resource.loading}
      error={error}
      onRefetch={() => refetch()}
      refetchHint="Click to refetch the tags"
    />
  );
}
